import React, { useState } from 'react';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ContactCard from './components/ContactCard';
import FAQItem from './components/FAQItem';
import ContactForm from './components/ContactForm';
import OfficeLocation from './components/OfficeLocation';
import EmergencyContact from './components/EmergencyContact';

const ContactSupport = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Advait Tour and Travels",
      "telephone": "+91-79986-66660",
      "email": "harnamnagpal7@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "h No -39, new shivpuri",
        "addressLocality": "Ludhiana",
        "addressRegion": "Punjab",
        "postalCode": "141008",
        "addressCountry": "IN"
      }
    }
  };

  const contactMethods = [
    {
      icon: 'MessageCircle',
      title: 'WhatsApp Support',
      description: 'Get instant responses on WhatsApp. Chat with our support team for quick assistance.',
      action: () => window.open('https://wa.me/917998666660?text=Hello%20Advait%20Tour%20and%20Travels,%20I%20need%20assistance%20with', '_blank'),
      actionLabel: 'Chat on WhatsApp',
      iconColor: '#25D366'
    },
    {
      icon: 'Phone',
      title: 'Call Us',
      description: 'Speak directly with our customer service representatives. Available 24/7 for your convenience.',
      action: () => window.open('tel:79986-66660', '_self'),
      actionLabel: 'Call 79986-66660',
      iconColor: 'var(--color-primary)'
    },
    {
      icon: 'Mail',
      title: 'Email Support',
      description: 'Send us detailed inquiries via email. We respond within 24 hours on business days.',
      action: () => window.open('mailto:Advaittourtravel666@gmail.com', '_self'),
      actionLabel: 'Send Email',
      iconColor: 'var(--color-secondary)'
    }
  ];

  const faqData = [
    {
      question: 'How do I book a taxi with Advait Tour and Travels?',
      answer: 'You can book through our website by selecting your service type, entering pickup and drop locations, choosing date and time, and completing the payment. Alternatively, contact us via WhatsApp at 79986-66660 or call us directly for instant booking assistance.'
    },
    {
      question: 'What is your cancellation policy?',
      answer: 'Free cancellation up to 24 hours before scheduled pickup. Cancellations within 24 hours incur a 25% charge. No-shows are charged 100%. Refunds are processed within 5-7 business days to the original payment method.'
    },
    {
      question: 'Are your drivers verified and trained?',
      answer: 'Yes, all our drivers undergo thorough background verification, possess valid commercial driving licenses, and complete mandatory safety training. They are experienced in North Indian routes and committed to passenger safety and comfort.'
    },
    {
      question: 'Do you provide services for corporate clients?',
      answer: 'Absolutely! We offer dedicated corporate travel solutions including bulk booking discounts, monthly billing, priority support, and customized travel packages. Contact our corporate desk at Advaittourtravel666@gmail.com for detailed information.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major payment methods including credit/debit cards, UPI, net banking, digital wallets, and cash. Online payments are secured with industry-standard encryption for your safety.'
    },
    {
      question: 'Can I modify my booking after confirmation?',
      answer: 'Yes, you can modify your booking up to 6 hours before scheduled pickup through your dashboard or by contacting support. Changes to date, time, or pickup location are subject to availability and may incur nominal charges.'
    },
    {
      question: 'What happens if my flight/train is delayed?',
      answer: 'We monitor flight and train schedules for airport and railway station pickups. Our drivers wait up to 60 minutes for flight delays and 30 minutes for train delays at no extra charge. Please inform us of any significant delays.'
    },
    {
      question: 'Do you provide child seats and wheelchair accessibility?',
      answer: 'Yes, we provide complimentary child seats upon request during booking. Wheelchair-accessible vehicles are available for Tempo Traveller and Force Urbania services. Please specify requirements at least 24 hours in advance.'
    }
  ];

  const officeLocations = [
    {
      city: 'Amritsar (Headquarters)',
      address: 'Golden Avenue, Near Golden Temple, Amritsar, Punjab 143001',
      phone: '79986-66660',
      hours: 'Open 24/7',
      isHeadquarters: true
    },
    {
      city: 'Chandigarh',
      address: 'Sector 17, Chandigarh, Punjab 160017',
      phone: '7998666661',
      hours: 'Mon-Sun: 6:00 AM - 10:00 PM'
    },
    {
      city: 'Ludhiana',
      address: 'Model Town, Ludhiana, Punjab 141002',
      phone: '7998666662',
      hours: 'Mon-Sun: 6:00 AM - 10:00 PM'
    },
    {
      city: 'Jalandhar',
      address: 'Civil Lines, Jalandhar, Punjab 144001',
      phone: '7998666663',
      hours: 'Mon-Sun: 6:00 AM - 10:00 PM'
    }
  ];

  const filteredFAQs = faqData?.filter(faq =>
    faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleCallbackRequest = () => {
    alert('Callback request feature coming soon! Please call us directly at 79986-66660 for immediate assistance.');
  };

  return (
    <>
      <SEO 
        title="Contact Advait Tour and Travels - 24/7 Customer Support | Chandigarh Taxi Service"
        description="Contact Advait Tour and Travels for taxi booking support in Chandigarh. Call +91-79986-66660, WhatsApp support, or email us. 24/7 customer service for all your travel needs."
        keywords="contact Advait Tour and Travels, taxi booking support, Chandigarh taxi contact, customer service, 24/7 support, WhatsApp taxi booking, emergency taxi contact"
        ogTitle="Contact Advait Tour and Travels - 24/7 Customer Support | Chandigarh Taxi Service"
        ogDescription="Contact Advait Tour and Travels for taxi booking support in Chandigarh. Call +91-79986-66660, WhatsApp support, or email us. 24/7 customer service for all your travel needs."
        ogUrl={typeof window !== 'undefined' ? window.location?.href : 'https://advaittravels.com/contact-support'}
        canonicalUrl={typeof window !== 'undefined' ? window.location?.href : 'https://advaittravels.com/contact-support'}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 lg:pt-24">
          <section className="gradient-hero py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
                  <Icon name="Headphones" size={18} />
                  <span>24/7 Support Available</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 md:mb-6">
                  We're Here to Help
                </h1>
                <p className="text-base md:text-lg text-white/90 leading-relaxed">
                  Your satisfaction is our priority. Reach out through your preferred channel and experience the warmth of Punjabi hospitality combined with professional service excellence.
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {contactMethods?.map((method, index) => (
                  <ContactCard key={index} {...method} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div>
                  <div className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                      Send Us a Message
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      Fill out the form below and our team will get back to you within 24 hours. For urgent matters, please call our emergency hotline.
                    </p>
                  </div>
                  <ContactForm />
                </div>

                <div className="space-y-8">
                  <EmergencyContact />

                  <div className="bg-card rounded-xl p-6 md:p-8 border border-border">
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                      Quick Actions
                    </h3>
                    <div className="space-y-4">
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="Calendar"
                        iconPosition="left"
                        onClick={() => window.location.href = '/booking-confirmation'}
                        fullWidth
                      >
                        Make a New Booking
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="LayoutDashboard"
                        iconPosition="left"
                        onClick={() => window.location.href = '/user-dashboard'}
                        fullWidth
                      >
                        View My Bookings
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="PhoneCall"
                        iconPosition="left"
                        onClick={handleCallbackRequest}
                        fullWidth
                      >
                        Request Callback
                      </Button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 md:p-8 border border-primary/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="Briefcase" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                          Corporate Inquiries
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                          Looking for bulk bookings or corporate travel solutions? Our dedicated team is ready to assist.
                        </p>
                        <Button
                          variant="default"
                          size="default"
                          iconName="Mail"
                          iconPosition="left"
                          onClick={() => window.open('mailto:corporate@advaittourandtravels.com', '_self')}
                        >
                          Contact Corporate Desk
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  Find quick answers to common questions. Can't find what you're looking for? Contact our support team.
                </p>
              </div>

              <div className="max-w-3xl mx-auto mb-8">
                <Input
                  type="search"
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>

              <div className="max-w-3xl mx-auto space-y-4">
                {filteredFAQs?.length > 0 ? (
                  filteredFAQs?.map((faq, index) => (
                    <FAQItem key={index} {...faq} />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No FAQs found matching your search. Try different keywords or contact support.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                  Our Office Locations
                </h2>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  Visit us at any of our offices across Punjab. Walk-ins welcome during business hours.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
                {officeLocations?.map((location, index) => (
                  <OfficeLocation key={index} {...location} />
                ))}
              </div>

              <div className="bg-card rounded-xl overflow-hidden border border-border">
                <div className="aspect-[16/9] md:aspect-[21/9] w-full">
                  <iframe
                    width="100%"
                    height="100%"
                    loading="lazy"
                    title="Advait Tour and Travels Headquarters Location"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps?q=31.6340,74.8723&z=14&output=embed"
                    className="border-0"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-gradient-journey">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <Icon name="MessageCircle" size={48} color="#FFD700" className="mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                Still Have Questions?
              </h2>
              <p className="text-base md:text-lg text-white/90 mb-8 leading-relaxed">
                Our friendly support team is available 24/7 to assist you with any queries or concerns.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => window.open('https://wa.me/917998666660', '_blank')}
                  className="bg-white text-primary hover:bg-white/90 whatsapp-pulse"
                >
                  Chat on WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => window.open('tel:7998666660', '_self')}
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10"
                >
                  Call 7998666660
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactSupport;